import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import ReactECharts from 'echarts-for-react'
import type { EChartsOption } from 'echarts'
import { objectsApi, type AnomalyHistoryPoint } from '../api'
import { getAnalyticsHex, getAnalyticsLabel } from '@/lib/utils/analytics-status'
import { Activity, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { appLocale } from '@/lib/dateUtils'
import { escapeHtml } from '@/lib/escape-html'

interface AnalyticsHistoryChartProps {
    objectID: string
    embedded?: boolean
}

// Anomaly score colour ramp — mirrors AnomalyHistoryChart so the two reads are consistent.
function getScoreColor(score: number): string {
    if (score >= 7) return '#dc2626'
    if (score >= 4) return '#ea580c'
    if (score >= 1) return '#f59e0b'
    return '#22c55e'
}

// Replicates vw_AnalyticsStatus's status -> code mapping, applied per version to its own scan JSON.
// This keeps the history's per-version disposition consistent with the versions table.
function statusCode(point: AnomalyHistoryPoint): number {
    let scanStatus: string | null = null
    let severity: Record<string, number> | null = null
    if (point.analyticsText) {
        try {
            const j = JSON.parse(point.analyticsText)
            scanStatus = typeof j?.status === 'string' ? j.status : null
            severity = j?.summary?.severity_counts ?? null
        } catch {
            /* fall through to numeric column */
        }
    }
    switch (scanStatus) {
        case 'threats_found': return 2
        case 'warnings_found': return 3
        case 'informational': return 5
        case 'clean': return 4
        case 'error': return 10
        case 'in_progress': return 1
        case null: break
        default: return 0
    }
    // No usable scan JSON — fall back to the numeric column the endpoint returned.
    void severity
    return point.analytics != null ? Number(point.analytics) : 0
}

function severityCounts(point: AnomalyHistoryPoint): Record<string, number> {
    if (!point.analyticsText) return {}
    try {
        const j = JSON.parse(point.analyticsText)
        return j?.summary?.severity_counts ?? {}
    } catch {
        return {}
    }
}

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'] as const
const SEVERITY_HEX: Record<string, string> = {
    critical: '#dc2626', high: '#ea580c', medium: '#f59e0b', low: '#eab308', info: '#94a3b8',
}

export function AnalyticsHistoryChart({ objectID, embedded = false }: AnalyticsHistoryChartProps) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    const [split, setSplit] = useState(false)

    const { data: rawData = [], isLoading } = useQuery({
        queryKey: ['objects', objectID, 'anomaly-history'],
        queryFn: async () => {
            const response = await objectsApi.getAnomalyHistory(objectID)
            const bd = response.data as any
            return (bd?.data || bd || []) as AnomalyHistoryPoint[]
        },
        enabled: !!objectID && (open || embedded),
    })

    // Count versions that carry either a threat/suspicious disposition or an anomaly — the things worth a second look.
    const flagged = useMemo(
        () => rawData.filter(d => d.anomalyScore > 0 || statusCode(d) === 2 || statusCode(d) === 3),
        [rawData],
    )
    const threatCount = useMemo(() => rawData.filter(d => statusCode(d) === 2).length, [rawData])

    const chartOption = useMemo((): EChartsOption => {
        if (rawData.length === 0) return {}

        const dates = rawData.map((d) => {
            if (!d.datetime) return ''
            const dt = new Date(d.datetime)
            return dt.toLocaleDateString(appLocale(), { month: 'short', day: 'numeric', year: '2-digit' })
        })

        const codes = rawData.map(statusCode)
        const anomalyVals = rawData.map(d => (d.anomalyScore != null ? Number(d.anomalyScore) : 0))

        // Status track: full-height background bands, one per backup, coloured by that version's disposition.
        // Rendered as markArea on a dedicated (hidden) series so it spans the grid height regardless of values.
        const statusBands: any[] = rawData.map((_, i) => ([
            { xAxis: Math.max(0, i - 0.5), itemStyle: { color: getAnalyticsHex(codes[i]), opacity: 0.55 } },
            { xAxis: Math.min(rawData.length - 1, i + 0.5) },
        ]))

        // Severity stacked bars (informational) — shows how noisy each scan was.
        const severityByVersion = rawData.map(severityCounts)
        const severitySeries = SEVERITY_ORDER.map((sev) => ({
            name: sev,
            type: 'bar',
            stack: 'severity',
            xAxisIndex: split ? 0 : 0,
            yAxisIndex: 0,
            barMaxWidth: 22,
            data: severityByVersion.map(s => Number(s?.[sev] ?? 0)),
            itemStyle: { color: SEVERITY_HEX[sev], opacity: 0.9 },
            z: 2,
        }))

        // Anomaly score line.
        const anomalySeries: any = {
            name: 'Anomaly',
            type: 'line',
            data: anomalyVals,
            xAxisIndex: split ? 1 : 0,
            yAxisIndex: split ? 1 : 1,
            smooth: false,
            symbol: 'circle',
            symbolSize: 5,
            lineStyle: { width: 2, color: '#64748b' },
            itemStyle: {
                color: (p: any) => getScoreColor(anomalyVals[p.dataIndex] ?? 0),
            },
            z: 3,
            markPoint: {
                data: rawData.flatMap((d, i) => d.anomalyScore > 0 ? [{
                    coord: [i, anomalyVals[i]],
                    symbol: 'pin',
                    symbolSize: d.anomalyScore >= 4 ? 38 : 28,
                    itemStyle: { color: getScoreColor(d.anomalyScore), borderColor: '#fff', borderWidth: 2 },
                    label: { show: true, formatter: `${d.anomalyScore}`, color: '#fff', fontSize: 10, fontWeight: 'bold', offset: [0, -2] },
                }] : []),
            },
        }

        const tooltip = {
            trigger: 'axis' as const,
            axisPointer: { type: 'cross' as const, label: { show: false } },
            formatter: (params: any) => {
                const arr = Array.isArray(params) ? params : [params]
                if (arr.length === 0) return ''
                const idx = arr[0].dataIndex
                const point = rawData[idx]
                if (!point) return ''
                const code = codes[idx]
                let html = `<div style="font-weight:600;margin-bottom:4px">${escapeHtml(dates[idx])}</div>`
                if (point.objectSecondary) html += `<div style="color:#888;font-size:11px;margin-bottom:4px">${escapeHtml(point.objectSecondary)} backup</div>`
                html += `<div><span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${getAnalyticsHex(code)};margin-right:6px"></span>${t('objects.versions.analyticsReport', 'Security Scan')}: <b>${getAnalyticsLabel(code)}</b></div>`
                const sev = severityCounts(point)
                const sevParts = SEVERITY_ORDER.filter(s => Number(sev?.[s] ?? 0) > 0).map(s => `${sev[s]} ${s}`)
                if (sevParts.length) html += `<div style="font-size:11px;color:#666;margin-top:2px">${escapeHtml(sevParts.join(' · '))}</div>`
                const scoreColor = getScoreColor(point.anomalyScore || 0)
                html += `<div style="margin-top:4px"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${scoreColor};margin-right:6px"></span>${t('objects.anomalyScore', 'Anomaly')}: <b>${point.anomalyScore || 0}/8</b></div>`
                return html
            },
        }

        // Label above the range slider so it's clear it's a draggable zoom control.
        const zoomHint = {
            type: 'text' as const,
            left: 'center' as const,
            bottom: 28,
            z: 10,
            silent: true,
            style: { text: t('objects.analyticsHistoryZoomHint', '↔ Drag to zoom the date range'), fill: '#94a3b8', fontSize: 10 },
        }

        if (split) {
            // Two stacked grids sharing the category x-axis: analytics on top, anomaly below.
            return {
                tooltip,
                legend: { show: false },
                axisPointer: { link: [{ xAxisIndex: 'all' }] },
                graphic: [zoomHint],
                grid: [
                    { left: 15, right: 15, top: 30, height: '42%', containLabel: true },
                    { left: 15, right: 15, top: '60%', bottom: 48, containLabel: true },
                ],
                xAxis: [
                    { type: 'category', gridIndex: 0, data: dates, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } },
                    { type: 'category', gridIndex: 1, data: dates, axisLabel: { fontSize: 9, color: '#888', rotate: 35 }, axisLine: { show: false }, axisTick: { show: false } },
                ],
                yAxis: [
                    { type: 'value', gridIndex: 0, name: t('objects.versions.findings', 'Findings'), nameTextStyle: { fontSize: 10, color: '#888' }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { fontSize: 10, color: '#888' } },
                    { type: 'value', gridIndex: 1, name: t('objects.anomalyScore', 'Anomaly'), nameTextStyle: { fontSize: 10, color: '#888' }, min: 0, max: 8, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { fontSize: 10, color: '#888' } },
                ],
                series: [
                    { ...severitySeries[0], markArea: { silent: true, data: statusBands } },
                    ...severitySeries.slice(1),
                    anomalySeries,
                ],
                dataZoom: [{ type: 'inside', xAxisIndex: [0, 1] }, { type: 'slider', xAxisIndex: [0, 1], height: 18, bottom: 6, showDataShadow: false }],
            }
        }

        // Overlaid: status bands + severity bars (left axis) with the anomaly line on the right axis.
        return {
            tooltip,
            legend: { show: false },
            graphic: [zoomHint],
            grid: { left: 15, right: 15, top: 30, bottom: 66, containLabel: true },
            xAxis: { type: 'category', data: dates, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { fontSize: 9, color: '#888', rotate: 35 } },
            yAxis: [
                { type: 'value', name: t('objects.versions.findings', 'Findings'), nameTextStyle: { fontSize: 10, color: '#888' }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { type: 'dashed', color: '#e5e7eb' } }, axisLabel: { fontSize: 10, color: '#888' } },
                { type: 'value', name: t('objects.anomalyScore', 'Anomaly'), nameTextStyle: { fontSize: 10, color: '#888' }, min: 0, max: 8, position: 'right', axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false }, axisLabel: { fontSize: 10, color: '#888' } },
            ],
            series: [
                { ...severitySeries[0], markArea: { silent: true, data: statusBands } },
                ...severitySeries.slice(1),
                anomalySeries,
            ],
            dataZoom: [{ type: 'inside' }, { type: 'slider', height: 18, bottom: 6, showDataShadow: false }],
        }
    }, [rawData, split, t])

    const chartBody = (
        <>
            {/* Controls: overlay / split toggle */}
            <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-0.5 p-0.5 rounded-lg border bg-muted/30">
                            <button
                                type="button"
                                onClick={() => setSplit(false)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${!split ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            >
                                {t('objects.analyticsHistoryOverlay', 'Overlaid')}
                            </button>
                            <button
                                type="button"
                                onClick={() => setSplit(true)}
                                className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${split ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                            >
                                {t('objects.analyticsHistorySplit', 'Split')}
                            </button>
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                            {t('objects.analyticsHistoryLegend', 'Background = scan disposition · bars = finding severity · line = anomaly score')}
                        </span>
                        {flagged.length > 0 && (
                            <span className="ml-auto text-xs text-muted-foreground">
                                {t('objects.analyticsHistoryFlaggedCount', '{{count}} flagged of {{total}} versions', { count: flagged.length, total: rawData.length })}
                            </span>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="flex items-center justify-center h-[400px] text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</div>
                    ) : rawData.length === 0 ? (
                        <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">{t('objects.versions.noAnalyticsData', 'No version data available')}</div>
                    ) : (
                        <ReactECharts key={split ? 'split' : 'overlay'} option={chartOption} notMerge={true} style={{ height: 420 }} opts={{ renderer: 'canvas' }} />
                    )}

                    {/* Flagged versions table */}
                    {flagged.length > 0 && (
                        <div className="mt-4">
                            <h4 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                                {t('objects.analyticsHistoryFlagged', 'Flagged versions')} ({flagged.length})
                            </h4>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-muted/50 border-b">
                                            <th className="text-left px-3 py-2 font-medium">{t('objects.versions.objectPrimary', 'Date')}</th>
                                            <th className="text-left px-3 py-2 font-medium">{t('objects.versions.objectSecondary', 'Version')}</th>
                                            <th className="text-left px-3 py-2 font-medium">{t('objects.versions.analyticsReport', 'Disposition')}</th>
                                            <th className="text-left px-3 py-2 font-medium">{t('objects.versions.findingsBySeverity', 'Severity')}</th>
                                            <th className="text-center px-3 py-2 font-medium">{t('objects.anomalyScore', 'Anomaly')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {flagged.map((v) => {
                                            const code = statusCode(v)
                                            const sev = severityCounts(v)
                                            const sevParts = SEVERITY_ORDER.filter(s => Number(sev?.[s] ?? 0) > 0).map(s => `${sev[s]} ${s}`)
                                            return (
                                                <tr key={v.id} className="border-b last:border-0 hover:bg-muted/30">
                                                    <td className="px-3 py-2 whitespace-nowrap">
                                                        {v.datetime ? new Date(v.datetime).toLocaleDateString(appLocale(), { month: 'short', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                                                    </td>
                                                    <td className="px-3 py-2">{v.objectSecondary || '-'}</td>
                                                    <td className="px-3 py-2">
                                                        <span className="inline-flex items-center gap-1.5">
                                                            {code === 10 ? (
                                                                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" style={{ color: getAnalyticsHex(code) }} />
                                                            ) : (
                                                                <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: getAnalyticsHex(code) }} />
                                                            )}
                                                            {getAnalyticsLabel(code)}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2 text-muted-foreground">{sevParts.join(' · ') || '-'}</td>
                                                    <td className="px-3 py-2 text-center">
                                                        {v.anomalyScore > 0 ? (
                                                            <Badge variant="outline" className="text-[10px]" style={{ borderColor: getScoreColor(v.anomalyScore), color: getScoreColor(v.anomalyScore) }}>
                                                                {v.anomalyScore}
                                                            </Badge>
                                                        ) : '-'}
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
        </>
    )

    if (embedded) {
        return (
            <div className="w-full">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <Activity className="h-4 w-4" />
                    {t('objects.analyticsHistory', 'Analytics History')} — {objectID}
                    {threatCount > 0 && (
                        <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 border-red-500 bg-red-500/20 text-red-700">
                            {threatCount}
                        </Badge>
                    )}
                </div>
                {chartBody}
            </div>
        )
    }

    return (
        <>
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="gap-1.5">
                <Activity className="h-4 w-4" />
                {t('objects.analyticsHistory', 'Analytics History')}
                {threatCount > 0 && (
                    <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 border-red-500 bg-red-500/20 text-red-700">
                        {threatCount}
                    </Badge>
                )}
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Activity className="h-5 w-5" />
                            {t('objects.analyticsHistory', 'Analytics History')} — {objectID}
                        </DialogTitle>
                    </DialogHeader>
                    {chartBody}
                </DialogContent>
            </Dialog>
        </>
    )
}
